// Utilities
import { Assessment, AssessmentResult, type AssessmentStatus, type ReviewCountsSummary } from '@/models/Assessment';
import { CoherenceCheck } from '@/models/CoherenceCheck';
import { DocRef } from '@/models/Models';
import { Task, type TaskReviewDecision, type TaskProgressStatus } from '@/models/Task';
import { TaskGroupingSubject } from '@/models/TaskGroupingSubject';
import { Topic, Question } from '@/models/Topic';
import { tdGroupForSubsection } from '@/models/td'
import type { TDRegulation } from '@/models/td'
import { useProjectsStore } from '@/stores/projects'
import { query, collection, addDoc, updateDoc, serverTimestamp, DocumentReference, onSnapshot, Unsubscribe, doc, orderBy, limit, QuerySnapshot, DocumentData, DocumentSnapshot, getDoc, getDocs, startAfter, where, writeBatch } from 'firebase/firestore';

import { defineStore, acceptHMRUpdate } from 'pinia'
import { useFirestore } from 'vuefire';
import { useAnalyticsStore } from './analytics'
import { useTDCurrentStore } from './tdCurrent'
import { useTasksStore } from './tasks'
import { useTaskDirectivesStore } from './taskDirectives'
import { trackAction } from '@/composables/useActivityTracker'
import { CsmAction } from '@/constants/csmActions'
import { useUsersStore } from './users'
import { logger } from '@/utils/logger'
import { companyLanguageField, runLanguageName, type RunLanguageName } from '@/utils/runLanguage'
import { isAcceptedKanbanTask, resolveUnacceptSourcePath, taskIdFromRef } from '@/utils/taskSourceOfTruth'
import { submitTaskCreate } from '@/composables/useTaskCreate'
import { submitPendingCommand } from '@/composables/useTaskPending'
import { reportOutcome } from '@/composables/useTaskCommandErrors'
import { proposalTarget, tdLatestProposalTarget, type ProposalTarget } from '@/utils/proposalTarget'
import { ProjectTask } from '@/models/ProjectTask'
import { submitTDTaskReview, type ContentMode } from '@/composables/useTaskCommands'

/**
 * What a send-back should do with a given path (IR-126 §4.3, phase 4.2a/4.2b).
 *
 * **Two outcomes since MYAC-1098, where there were three.** `nothing` existed
 * because a manual task could not be sent back at all — `task_unapprove`
 * refused it, so the only thing left to protect was the batch, which would
 * have written a review decision onto a task that was never proposed.
 *
 * The refusal is gone. A task with no proposal is rejected like any other; the
 * command simply has no proposal half to write. So the third outcome stopped
 * meaning "there is nothing to do here" and started meaning "do nothing" —
 * which is what it did: the command was never written, and the card sat where
 * it was with no label, no revision, and Reject still offered.
 */
type SendBackRoute =
  | { via: 'command'; projectId: string; taskId: string }
  | { via: 'batch' }
import i18n from '@/plugins/i18n'

const t = i18n.global.t

const LIMIT_LIVE = 10

/**
 * How one queued revival ended (MYAC-1557). `cancelled` covers the dismissed
 * dialog AND the rows a dismissed sweep dropped: the task is still parked.
 */
export type ReapproveOutcome = 'revived' | 'cancelled' | 'failed'

/**
 * Who waits on a queued revival, by task id. Outside the state: these are
 * callbacks, not data, and nothing renders them. The action review needs the
 * answer — the dialog is global and returns nothing to the gesture that opened
 * it, so before this a cancelled « Réintégrer » left the row « en cours » for good.
 */
const reapproveWaiters = new Map<string, Array<(outcome: ReapproveOutcome) => void>>()

function waitForReapprove(taskId: string): Promise<ReapproveOutcome> {
  return new Promise((resolve) => {
    reapproveWaiters.set(taskId, [...(reapproveWaiters.get(taskId) ?? []), resolve])
  })
}

function settleReapprove(taskId: string, outcome: ReapproveOutcome): void {
  const waiters = reapproveWaiters.get(taskId)
  reapproveWaiters.delete(taskId)
  waiters?.forEach((resolve) => resolve(outcome))
}

// MYAC-775 — statuses that mean a run has definitively ended. Listed explicitly
// (rather than "not running/scheduled") so an absent or `unknown` status never
// counts as finished: holding the stop guard too long is harmless, releasing it
// while the run is still tearing down is the bug this guard prevents.
const TERMINAL_ASSESSMENT_STATUSES = new Set<AssessmentStatus>(['stopped', 'done', 'failed'])

function buildReviewCounts(tasks: Task[]): ReviewCountsSummary {
  return {
    total: tasks.length,
    pending: tasks.filter((task) => task.effectiveDecision === 'pending').length,
    accepted: tasks.filter((task) => task.effectiveDecision === 'accepted').length,
    rejected: tasks.filter(
      (task) => task.effectiveDecision === 'rejected' || task.effectiveDecision === 'directive_rejected',
    ).length,
    todo: tasks.filter((task) => task.effectiveProgress === 'todo').length,
    in_progress: tasks.filter((task) => task.effectiveProgress === 'in_progress').length,
    done: tasks.filter((task) => task.effectiveProgress === 'done').length,
    cancelled: tasks.filter((task) => task.effectiveProgress === 'cancelled').length, // legacy state — no longer produced
  }
}

/**
 * MYAC-719: once accepted, a task's source-of-truth is the kanban ProjectTask.
 * Swap accepted source tasks (those mirrored to a kanban copy) for their live
 * kanban task so the assessment/TD views show — and edit — the kanban doc.
 * Falls back to the source task when the kanban copy isn't loaded yet.
 */
function overlayAcceptedWithKanban(tasks: Task[]): Task[] {
  const kanban = useTasksStore()
  // Read the plain `allAcceptedTasks` getter (not the parametrized `taskById`)
  // so this overlay reactively re-runs when the kanban store resolves tasks.
  const byId = new Map(kanban.allAcceptedTasks.map((k) => [k.id, k]))
  return tasks.map((t) => (isAcceptedKanbanTask(t) ? (byId.get(t.id) ?? t) : t))
}

/**
 * The counts, from one definition — MYAC-992 (IR-126 4.6).
 *
 * The two writers of `aggregate_review_counts` disagreed, and the user saw it:
 * accept a task and the counter bar changed to numbers reflecting no work
 * anybody had done; reopen the assessment and it changed back.
 *
 * `recalculateAggregateReviewCountsFromTaskPath` re-read the proposals raw from
 * Firestore and counted those, while `persistAggregateReviewCountsOnInitialLoad`
 * went through `allTasks`, which applies {@link overlayAcceptedWithKanban}. So
 * `todo` / `in_progress` / `done` meant *real kanban progress* after a detail
 * load and *stale proposal-side progress* after an accept — same assessment,
 * two numbers, last writer wins.
 *
 * MYAC-719 settled which is correct: once accepted, a task's authoritative copy
 * is the kanban `ProjectTask`. So the overlay belongs on both paths, and the
 * only way to keep it on both is for neither to count without it.
 *
 * **Safe to call on an already-overlaid list.** The overlay joins on task id and
 * the kanban copy shares the source task's id (§7.3), so a second pass returns
 * the same objects — which is what lets `persistAggregateReviewCountsOnInitialLoad`
 * keep passing `allTasks` without either double-counting or having to reach for
 * a raw list that the getter does not expose.
 */
export function computeReviewCounts(tasks: Task[]): ReviewCountsSummary {
  return buildReviewCounts(overlayAcceptedWithKanban(tasks))
}

function areReviewCountsEqual(a?: ReviewCountsSummary | null, b?: ReviewCountsSummary | null): boolean {
  if (!a || !b) return false
  return a.total === b.total
    && a.pending === b.pending
    && a.accepted === b.accepted
    && a.rejected === b.rejected
    && a.todo === b.todo
    && a.in_progress === b.in_progress
    && a.done === b.done
    && a.cancelled === b.cancelled
}

const ACCEPT_CONCURRENCY = 10
/**
 * `Promise.all` with a ceiling.
 *
 * Preserves input order in the result and never rejects for the caller's
 * benefit — `submitTaskCreate` reports its own outcome and resolves `false`, so
 * a rejection here would mean something else went wrong and should surface.
 *
 * One consequence of letting it surface: the other workers keep pulling from
 * `next`, so creates can still be submitted after the caller has already
 * toasted and rethrown. Accepted rather than overlooked — the alternative is a
 * shared abort flag guarding a case that needs `submitTaskCreate` to throw
 * before its own `try`, which today means only `markPending`.
 */
async function mapWithLimit<T, R>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<R>,
): Promise<R[]> {
  const out = new Array<R>(items.length)
  let next = 0
  const worker = async (): Promise<void> => {
    for (let i = next++; i < items.length; i = next++) {
      out[i] = await fn(items[i])
    }
  }
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker))
  return out
}

/**
 * A command the apply layer refused, which has already been reported.
 *
 * Exists only so the generic handler can tell "something broke" from "the
 * server said no, and the user has been told why". Both still throw — callers
 * close their dialogs on the success path alone — but a refusal that already
 * raised a specific message must not collect a vaguer one on top of it.
 */
class CommandRefused extends Error {
  override readonly name = 'CommandRefused'
}

export const useAssessmentsStore = defineStore('assessments', {
  state: () => ({
    isLoading: false,
    isLoadingMore: false,
    isLoadingTopic: false,
    // MYAC-775 — ids of assessments with a STOP command already enqueued. Kept in
    // the store (not a per-component ref) so the disabled state survives remounts
    // and is shared by every Stop control for the same assessment (QMS view, TD
    // view, TD run popover). An id is released by `reconcileStoppingIds`, which
    // each surface owning an assessment subscription calls with what it observes.
    stoppingIds: new Set<string>(),
    _current_pid: '',
    _current_aid: '',
    assessmentsSnapshots: [] as QuerySnapshot<DocumentData, DocumentData>[],
    _assessments_unsub: null as Unsubscribe | null,
    hasMoreAssessments: false,
    _lastAssessmentDoc: null as DocumentSnapshot<DocumentData, DocumentData> | null,
    assessmentShapshot: null as DocumentSnapshot<DocumentData, DocumentData> | null,
    _assessment_unsub: null as Unsubscribe | null,
    assessmentResultsSnapshots: null as QuerySnapshot<DocumentData, DocumentData> | null,
    _assessment_results_unsub: null as Unsubscribe | null,
    topicSnapshot: null as DocumentSnapshot<DocumentData, DocumentData> | null,
    _topic_unsub: null as Unsubscribe | null,
    topicQuestionsSnapshots: null as QuerySnapshot<DocumentData, DocumentData> | null,
    _topic_questions_unsub: null as Unsubscribe | null,
    tasksSnapshots: null as QuerySnapshot<DocumentData, DocumentData> | null,
    _tasks_unsub: null as Unsubscribe | null,
    /**
     * The re-approval waiting on a keep-or-reset answer (§5.4).
     *
     * Held in the store, not in a component, for the reason the conflict
     * dialog is: accept is offered from four proposal surfaces, and a prompt
     * mounted per surface is four chances to forget one. `App.vue` mounts the
     * dialog once and it serves all of them.
     */
    reapprovePrompt: null as
      | {
          /**
           * The SOURCE proposal doc, whose aggregates are recounted once the
           * revival lands.
           *
           * **Empty for an action-plan revival (4.8a).** A remediation row is a
           * projected `views/**` doc, not a proposal: it carries no review
           * aggregates, and `recalculateAggregateReviewCountsFromTaskPath` would
           * be asked to recount a document that has nothing to count. Empty
           * means "no aggregates behind this one", and `resolveReapprove` skips
           * that step rather than the caller having to know it should.
           */
          taskPath: string
          projectId: string
          taskId: string
          baseSeq: number
          /**
           * `reset` was tried and refused, so only `keep` remains (§5.8).
           *
           * 4.2b made the apply layer refuse a reset whose genesis was
           * reconstructed from the task itself — reverting to it would revert
           * nothing — and the refusal ends "Re-approve with content_mode
           * 'keep'". 4.3 is what makes that reachable, because 4.3 ships the
           * button, so shipping it without a path out of the refusal is this
           * card's to fix even though the general `policy` message is not.
           */
          resetRefused?: boolean
          /**
           * What this task's proposal situation is, and therefore whether there
           * is a question to ask at all (MYAC-1098).
           *
           * Re-approval used to have exactly one shape because every revivable
           * task had a proposal — un-approve and re-approve refused the rest,
           * and archive was their exit. Now that they do not, two of the three
           * situations have only ONE content mode available: there is no
           * original to revert to, so `keep` is not a choice, it is the answer.
           *
           * - `none` — no proposal, and there never was one. Nothing to ask, so
           *   nothing is asked: the dialog answers `keep` itself.
           * - `deleted` — there was one and it is gone. Same single answer, but
           *   the user is TOLD, because the task changing character is a fact
           *   they did not cause and would otherwise discover later.
           * - `present` (and `undefined`) — the ordinary question.
           *
           * Undefined rather than required so the proposal-driven callers stay
           * correct without restating what is true of them by construction: a
           * prompt raised FROM a proposal has one.
           */
          proposal?: 'present' | 'deleted' | 'none'
        }
      | null,
    /**
     * The OTHER parked rows a single revival choice also covers (4.8a).
     *
     * `reapprovePrompt` is ONE slot and the dialog shows ONE row, which was fine
     * while accept was a single-row gesture. The Action Plan's bulk accept-all
     * fans out ten at a time, and a sweep over a group containing several PARKED
     * rows — exactly what this feature's undo produces — had every one of them
     * assigning that slot. Last write won, the other rows reported success, and
     * their tasks were never revived: the rows simply did not move.
     *
     * So the extras collect here and the dialog asks ONCE — "keep the edits or
     * revert to the original" is a question about the sweep, not about each row,
     * and ten identical modals in a row is a worse answer than none. The chosen
     * mode applies to every task in the batch.
     */
    reapproveQueue: [] as Array<{
      taskPath: string
      projectId: string
      taskId: string
      baseSeq: number
      resetRefused?: boolean
      /** See `reapprovePrompt.proposal` — the queue carries the same fact. */
      proposal?: 'present' | 'deleted' | 'none'
    }>,
    allTasksMap: new Map<string, Task[]>(),
    _all_tasks_unsubs: [] as Unsubscribe[],
    _all_tasks_pid: '' as string,
    _all_tasks_aid: '' as string,
    isLoadingAllTasks: false,
    coherenceChecksSnapshots: null as QuerySnapshot<DocumentData, DocumentData> | null,
    _coherence_checks_unsub: null as Unsubscribe | null,
    taskGroupingSubjects: [] as TaskGroupingSubject[],
    _task_grouping_unsub: null as Unsubscribe | null,
    _task_grouping_pid: '' as string,
    _task_grouping_aid: '' as string,
    /**
     * IR-116 §5 — per-project × track legacy existence, keyed `${pid}|${track}`.
     * A Map in state so it is reactive: the nav reads it synchronously through
     * `legacyProjectFlag` and must re-render when the probe resolves.
     */
    _legacyExistence: new Map<string, boolean>(),
  }),
  getters: {
    assessments: (state) => state.assessmentsSnapshots.flatMap((s) => s?.docs.map((d) => Assessment.fromFirebase(d, null)).filter((d) => d != null)),
    assessment: (state) => state.assessmentShapshot?.data() ? Assessment.fromFirebase(state.assessmentShapshot, state.assessmentResultsSnapshots) : null,
    topic: (state) => state.topicSnapshot?.data() ? Topic.fromFirebase(state.topicSnapshot, state.topicQuestionsSnapshots) : null,
    tasks: (state) => state.tasksSnapshots?.docs ? state.tasksSnapshots?.docs.map((d) => Task.fromFirebase(d)).filter((d) => d != null) : [],
    allTasks: (state): Task[] => {
      const merged: Task[] = []
      state.allTasksMap.forEach((tasks) => merged.push(...tasks))
      return overlayAcceptedWithKanban(merged)
    },
    tasksByResultId: (state) => (resultId: string): Task[] => overlayAcceptedWithKanban(state.allTasksMap.get(resultId) ?? []),
    coherenceChecks: (state) =>
      state.coherenceChecksSnapshots?.docs
        ? state.coherenceChecksSnapshots.docs.map((d) => CoherenceCheck.fromFirebase(d)).filter((d): d is CoherenceCheck => d !== null)
        : [],
    /**
     * The memoized answer, read WITHOUT awaiting — `undefined` means "never
     * probed", which callers must distinguish from `false` ("probed, no legacy
     * data"). The router guards await `hasLegacyAssessments`; templates and nav
     * computeds read this.
     */
    legacyProjectFlag: (state) => (projectId: string, track: 'qms' | 'td'): boolean | undefined =>
      state._legacyExistence.get(`${projectId}|${track}`),
  },
  actions: {
    recalculateAggregateReviewCountsFromTaskPath: async function (taskPath: string) {
      const match = taskPath.match(/^projects\/([^/]+)\/assessments\/([^/]+)\/results\/[^/]+\/tasks\/[^/]+$/)
      if (!match) {
        return
      }

      const [, pid, aid] = match
      const db = useFirestore()
      const resultDocs = await getDocs(collection(db, `projects/${pid}/assessments/${aid}/results`))

      const taskSnapshots = await Promise.all(
        resultDocs.docs.map((resultDoc) => getDocs(collection(db, `${resultDoc.ref.path}/tasks`))),
      )

      const tasks = taskSnapshots
        .flatMap((snapshot) => snapshot.docs)
        .map((docSnapshot) => Task.fromFirebase(docSnapshot))
        .filter((task): task is Task => task !== null)

      // Through the shared definition, so this path gets the MYAC-719 overlay
      // it used to omit — the whole of MYAC-992's defect.
      const counts = computeReviewCounts(tasks)

      await updateDoc(doc(db, `projects/${pid}/assessments/${aid}`), {
        aggregate_review_counts: counts,
        updated_at: serverTimestamp(),
      })
    },
    persistAggregateReviewCountsOnInitialLoad: async function (pid: string, aid: string) {
      try {
        if (!pid || !aid) return
        if (this._all_tasks_pid !== pid || this._all_tasks_aid !== aid) return

        const tasks = this.allTasks
        const counts = computeReviewCounts(tasks)
        const currentCounts = this.assessment?.aggregate_review_counts
        if (areReviewCountsEqual(currentCounts, counts)) {
          return
        }

        const db = useFirestore()
        await updateDoc(doc(db, `projects/${pid}/assessments/${aid}`), {
          aggregate_review_counts: counts,
          updated_at: serverTimestamp(),
        })
      } catch (error) {
        logger.error('Error persisting initial aggregate review counts', error)
      }
    },
    unsubscribeAllTasks: function () {
      this._all_tasks_unsubs.forEach((unsub) => {
        try {
          unsub()
        } catch (error) {
          logger.error('Error unsubscribing all tasks listener', error)
        }
      })
      this._all_tasks_unsubs = []
      this._all_tasks_pid = ''
      this._all_tasks_aid = ''
      this.isLoadingAllTasks = false
      this.allTasksMap = new Map()
    },
    subscribeAllTasks: async function (pid: string, aid: string) {
      if (
        this._all_tasks_unsubs.length > 0
        && this._all_tasks_pid === pid
        && this._all_tasks_aid === aid
      ) {
        return
      }

      this.unsubscribeAllTasks()

      if (!pid || !aid) {
        return
      }

      // MYAC-719: kanban tasks back the accepted-task overlay (allTasks / tasksByResultId).
      void useTasksStore().loadKanbanTasks(pid)

      this._all_tasks_pid = pid
      this._all_tasks_aid = aid
      this.isLoadingAllTasks = true

      const db = useFirestore()
      const resultsSnapshot = await getDocs(
        collection(db, `projects/${pid}/assessments/${aid}/results`),
      )

      if (resultsSnapshot.empty) {
        this.isLoadingAllTasks = false
        return
      }

      let pendingInitialSnapshots = resultsSnapshot.docs.length
      const nextMap = new Map<string, Task[]>()

      resultsSnapshot.docs.forEach((resultDoc) => {
        const tasksQuery = query(collection(db, `${resultDoc.ref.path}/tasks`))
        const unsub = onSnapshot(tasksQuery, (tasksSnapshot) => {
          const tasks = tasksSnapshot.docs
            .map((d) => Task.fromFirebase(d))
            .filter((d): d is Task => d !== null)

          nextMap.set(resultDoc.id, tasks)
          this.allTasksMap = new Map(nextMap)

          if (pendingInitialSnapshots > 0) {
            pendingInitialSnapshots -= 1
            if (pendingInitialSnapshots === 0) {
              this.isLoadingAllTasks = false
              void this.persistAggregateReviewCountsOnInitialLoad(pid, aid)
            }
          }
        })
        this._all_tasks_unsubs.push(unsub)
      })
    },
    createAssessment: async function (pid: string, clauses: DocumentReference[] | null = null, enableCoherenceCheck = false): Promise<DocumentReference> {
      this.isLoading = true
      const firestore = useFirestore()
      const usersStore = useUsersStore()
      const ref = collection(firestore, `projects/${pid}/assessments/`)
      const userRef = doc(firestore, 'users', usersStore.uid)
      const docRef = await addDoc(ref, {
        status: "draft",
        kind: "standard",
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        clauses: clauses,
        created_by: userRef,
        enable_coherence_check: enableCoherenceCheck,
        ...companyLanguageField(),
      })
      this.isLoading = false
      try { useAnalyticsStore().trackEvent('assessment_create_success', { pid, aid: docRef.id }) } catch { }
      try { trackAction(CsmAction.AssessmentStarted, { projectId: pid }) } catch { }
      return docRef;
    },
    createCapaExtraction: async function (pid: string): Promise<DocumentReference> {
      // MYAC-711 — trigger the CAPA list extraction: write an Assessment doc with
      // kind 'qms_capa_list', then flip to 'scheduled' so Eventarc fires and the
      // backend runs QMSService.aextract_capa_list (qms_orchestrate_agent).
      this.isLoading = true
      const firestore = useFirestore()
      const usersStore = useUsersStore()
      const ref = collection(firestore, `projects/${pid}/assessments/`)
      const userRef = doc(firestore, 'users', usersStore.uid)
      const docRef = await addDoc(ref, {
        status: 'draft',
        kind: 'qms_capa_list',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userRef,
        language: runLanguageName(),
      })
      await updateDoc(docRef, {
        status: 'scheduled',
        updated_at: serverTimestamp(),
      })
      this.isLoading = false
      return docRef
    },
    createNcExtraction: async function (pid: string): Promise<DocumentReference> {
      // Trigger the NC list extraction: write an Assessment doc with kind
      // 'qms_nc_list', then flip to 'scheduled' so Eventarc fires and the
      // backend runs QMSService.aextract_nc_list (qms_orchestrate_agent).
      this.isLoading = true
      const firestore = useFirestore()
      const usersStore = useUsersStore()
      const ref = collection(firestore, `projects/${pid}/assessments/`)
      const userRef = doc(firestore, 'users', usersStore.uid)
      const docRef = await addDoc(ref, {
        status: 'draft',
        kind: 'qms_nc_list',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userRef,
        language: runLanguageName(),
      })
      await updateDoc(docRef, {
        status: 'scheduled',
        updated_at: serverTimestamp(),
      })
      this.isLoading = false
      return docRef
    },
    createChangeControlExtraction: async function (pid: string): Promise<DocumentReference> {
      // MYAC-899 — trigger the change-control list extraction: write an Assessment
      // doc with kind 'qms_change_control_list', then flip to 'scheduled' so
      // Eventarc fires and the backend runs QMSService.aextract_change_control_list
      // (qms_orchestrate_agent).
      this.isLoading = true
      const firestore = useFirestore()
      const usersStore = useUsersStore()
      const ref = collection(firestore, `projects/${pid}/assessments/`)
      const userRef = doc(firestore, 'users', usersStore.uid)
      const docRef = await addDoc(ref, {
        status: 'draft',
        kind: 'qms_change_control_list',
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userRef,
        language: runLanguageName(),
      })
      await updateDoc(docRef, {
        status: 'scheduled',
        updated_at: serverTimestamp(),
      })
      this.isLoading = false
      return docRef
    },
    createAssessmentFromDocRefs: async function (pid: string, refs: DocRef[], enableCoherenceCheck = false): Promise<DocumentReference> {
      // Helper to keep Firestore usage inside the store; converts DocRef -> DocumentReference and delegates to createAssessment
      const firestore = useFirestore()
      const clauseRefs = (refs || [])
        .map((r) => r?.toDocumentReference(firestore))
        .filter((r): r is DocumentReference => r != null)
      return await this.createAssessment(pid, clauseRefs, enableCoherenceCheck)
    },
    /**
     * Follows one running assessment (a TD run) and releases its Stop guard when
     * the run reaches a terminal status (MYAC-775; the other path is the TD doc
     * clearing `running_assessment_id`). Returns the stop function.
     */
    watchRunningAssessment(pid: string, assessmentId: string, next: (assessment: Assessment | null) => void): () => void {
      return onSnapshot(doc(useFirestore(), `projects/${pid}/assessments/${assessmentId}`), (snap) => {
        const assessment = snap.exists() ? Assessment.fromFirebase(snap, null) : null
        next(assessment)
        if (assessment) this.reconcileStoppingIds([assessment])
      })
    },
    /**
     * Follows the newest assessment of one kind (by creation date), e.g. the
     * supplier-list extraction a button reflects as running or failed. Single
     * filter (kind), newest picked client-side: index-free. Returns the stop function.
     */
    watchLatestAssessmentOfKind(pid: string, kind: string, next: (latest: Assessment | null) => void): () => void {
      const q = query(collection(useFirestore(), `projects/${pid}/assessments/`), where('kind', '==', kind))
      return onSnapshot(q, (snap) => {
        const docs = snap.docs
          .map((d) => Assessment.fromFirebase(d, null))
          .filter((a): a is Assessment => a !== null)
        docs.sort((a, b) => (b.created_at?.getTime?.() ?? 0) - (a.created_at?.getTime?.() ?? 0))
        next(docs[0] ?? null)
      })
    },
    /** « Lancer » on the legacy list: create the assessment from the picked clauses, read it back, schedule it. One gesture, one action. */
    launchAssessmentFromDocRefs: async function (pid: string, refs: DocRef[], enableCoherenceCheck = false): Promise<void> {
      const ref = await this.createAssessmentFromDocRefs(pid, refs, enableCoherenceCheck)
      const assessment = Assessment.fromFirebase(await getDoc(ref), null)
      if (assessment) await this.scheduleAssessment(assessment)
    },
    updateAssessment: async function (assessment: Assessment) {
      this.isLoading = true
      const firestore = useFirestore()
      await updateDoc(assessment.toDocumentReference()!, {
        updated_at: serverTimestamp(),
        clauses: assessment?.clauses?.map((d) => d.toDocumentReference(firestore)),
        documents: {
          quality_manual: assessment?.documents?.quality_manual?.toDocumentReference(firestore),
          procedures: assessment?.documents?.procedures?.map((d) => d.toDocumentReference(firestore)),
        }
      });
      this.isLoading = false
      try { useAnalyticsStore().trackEvent('assessment_update', { pid: this._current_pid, aid: assessment.id }) } catch { }
    },
    scheduleAssessment: async function (assessment: Assessment) {
      this.isLoading = true
      await updateDoc(assessment.toDocumentReference()!, {
        status: 'scheduled',
        updated_at: serverTimestamp(),
      });
      this.isLoading = false
      try { useAnalyticsStore().trackEvent('assessment_schedule', { pid: this._current_pid, aid: assessment.id }) } catch { }
    },
    duplicateAssessment: async function (pid: string, assessment: Assessment): Promise<DocumentReference> {
      this.isLoading = true
      const firestore = useFirestore()
      const doc = await this.createAssessment(
        pid,
        assessment.clauses.map((d) => d.toDocumentReference(firestore)!),
        assessment.enable_coherence_check ?? false
      )
      this.isLoading = false
      try { useAnalyticsStore().trackEvent('assessment_duplicate', { pid, aid: assessment.id }) } catch { }
      return doc;
    },
    getAssessment: async function (pid: string, aid: string, forceUpdate = false) {
      return new Promise((resolve, reject) => {
        try {
          if (
            this._assessment_unsub &&
            this._assessment_results_unsub &&
            !forceUpdate &&
            this._current_pid === pid &&
            this._current_aid === aid
          ) {
            // if the pid is the same, we can reuse the subscription
            return resolve(true);
          }
          this.isLoading = true
          if (this._assessment_unsub) {
            this._assessment_unsub()
            this._assessment_unsub = null
          }
          if (this._assessment_results_unsub) {
            this._assessment_results_unsub()
            this._assessment_results_unsub = null
          }
          if (this._coherence_checks_unsub) {
            this._coherence_checks_unsub()
            this._coherence_checks_unsub = null
          }
          this.unsubscribeAllTasks()
          if (!pid || !aid) {
            this.assessmentShapshot = null
            this.assessmentResultsSnapshots = null
            this.isLoading = false
            reject(false)
            return
          }
          this._current_pid = pid;
          this._current_aid = aid;
          const q = doc(useFirestore(), `projects/${pid}/assessments/${aid}`);
          this._assessment_unsub = onSnapshot(q, (s) => {
            this.assessmentShapshot = s
            // MYAC-775 — release the Stop guard once the run actually ends.
            if (this.assessment) this.reconcileStoppingIds([this.assessment])
            if (this._assessment_results_unsub) {
              this._assessment_results_unsub()
              this._assessment_results_unsub = null
            }
            const qc = query(collection(useFirestore(), `projects/${pid}/assessments/${aid}/results`))
            this._assessment_results_unsub = onSnapshot(qc, (r) => {
              this.assessmentResultsSnapshots = r
              this.isLoading = false
              resolve(true)
            })
          })
        } catch (error) {
          logger.error("Error getting assessment", error);
          this.isLoading = false
          reject(error)
        }
      })
    },
    getTasks(result_summary: AssessmentResult) {
      this.isLoading = true
      if (this._tasks_unsub) {
        this._tasks_unsub()
        this._tasks_unsub = null
      }
      if (!result_summary) {
        this.tasksSnapshots = null
        this.isLoading = false
        return
      }
      const q = query(collection(useFirestore(), `${result_summary.path}/tasks`))
      this._tasks_unsub = onSnapshot(q, (r) => {
        this.tasksSnapshots = r
        this.isLoading = false
      })
    },
    getTopic(result_summary: AssessmentResult, topic: DocRef) {
      this.isLoadingTopic = true
      this.topicQuestionsSnapshots = null
      if (this._topic_unsub) {
        this._topic_unsub()
        this._topic_unsub = null
      }
      if (!topic) {
        this.topicSnapshot = null
        this.isLoadingTopic = false
        return
      }
      const q = topic.toDocumentReference(useFirestore())!;
      this._topic_unsub = onSnapshot(q, (s) => {
        this.getTasks(result_summary);
        this.topicSnapshot = s
        if (this._topic_questions_unsub) {
          this._topic_questions_unsub()
          this._topic_questions_unsub = null
        }
        const qc = query(collection(useFirestore(), `${topic.path}/questions`))
        this._topic_questions_unsub = onSnapshot(qc, (r) => {
          this.topicQuestionsSnapshots = r
          this.isLoadingTopic = false
        })
      })
    },
    assessments_unsub: function () {
      if (this._assessments_unsub) {
        this._assessments_unsub()
        this._assessments_unsub = null
      }
    },
    listAssessments: async function (pid: string, forceUpdate = false) {
      if (this._assessments_unsub && !forceUpdate && this._current_pid === pid) {
        return Promise.resolve();
      }
      this.isLoading = true
      this.assessments_unsub()
      if (!pid) {
        this.assessmentsSnapshots = []
        this.hasMoreAssessments = false
        this._lastAssessmentDoc = null
        this.isLoading = false
        return
      }
      this._current_pid = pid;
      // Fetch one extra to detect if more exist
      // Filter to only show 'standard' assessments
      const q = query(
        collection(useFirestore(), `projects/${pid}/assessments`),
        where('kind', '==', 'standard'),
        orderBy("updated_at", 'desc'),
        limit(LIMIT_LIVE + 1)
      );
      this._assessments_unsub = onSnapshot(q, (a) => {
        const docs = a.docs
        this.hasMoreAssessments = docs.length > LIMIT_LIVE
        // Only keep LIMIT_LIVE documents, store last for pagination
        if (docs.length > LIMIT_LIVE) {
          this._lastAssessmentDoc = docs[LIMIT_LIVE - 1]
        } else if (docs.length > 0) {
          this._lastAssessmentDoc = docs[docs.length - 1]
        } else {
          this._lastAssessmentDoc = null
        }
        // Create a subset snapshot-like object with only LIMIT_LIVE docs
        const limitedSnapshot = {
          ...a,
          docs: docs.slice(0, LIMIT_LIVE)
        } as QuerySnapshot<DocumentData, DocumentData>
        this.assessmentsSnapshots = [limitedSnapshot]
        // MYAC-775 — release the Stop guard once a run actually ends.
        this.reconcileStoppingIds(this.assessments)
        this.isLoading = false
      })
    },
    /**
     * Does this project have LEGACY assessment data on this track? (IR-116 §5)
     *
     * The per-project cutoff has no field to read (current-state-map §5), so it
     * is derived from the data itself: one `limit(1)` existence query per
     * project × track, memoized for the session. Not `listAssessments`, for two
     * reasons — it subscribes a live listener for a question that needs a single
     * read, and it filters `kind == 'standard'`, which is blind to TD (review B5).
     *
     * FAILS SAFE: a refused or failed read answers `true` (legacy). Hiding the
     * legacy surface from a project that has data is the damaging direction;
     * showing one extra nav item on a fresh project is not. A failure is
     * deliberately NOT memoized, so a transient error does not pin the wrong
     * answer for the whole session.
     */
    async hasLegacyAssessments(projectId: string, track: 'qms' | 'td'): Promise<boolean> {
      if (!projectId) return false
      const key = `${projectId}|${track}`
      const cached = this._legacyExistence.get(key)
      if (cached !== undefined) return cached
      const kind = track === 'qms' ? 'standard' : 'technical_documentation'
      try {
        const snapshot = await getDocs(
          query(
            collection(useFirestore(), `projects/${projectId}/assessments`),
            where('kind', '==', kind),
            limit(1),
          ),
        )
        const exists = !snapshot.empty
        this._legacyExistence.set(key, exists)
        return exists
      } catch (error) {
        logger.warn('hasLegacyAssessments probe failed; treating the project as legacy', error)
        return true
      }
    },
    loadMoreAssessments: async function () {
      if (!this._current_pid || !this._lastAssessmentDoc || !this.hasMoreAssessments) {
        return
      }
      this.isLoadingMore = true
      const firestore = useFirestore()
      const q = query(
        collection(firestore, `projects/${this._current_pid}/assessments`),
        where('kind', '==', 'standard'),
        orderBy("updated_at", 'desc'),
        startAfter(this._lastAssessmentDoc),
        limit(LIMIT_LIVE + 1)
      )
      const snapshot = await getDocs(q)
      const docs = snapshot.docs
      this.hasMoreAssessments = docs.length > LIMIT_LIVE
      if (docs.length > LIMIT_LIVE) {
        this._lastAssessmentDoc = docs[LIMIT_LIVE - 1]
      } else if (docs.length > 0) {
        this._lastAssessmentDoc = docs[docs.length - 1]
      } else {
        this._lastAssessmentDoc = null
      }
      // Append limited docs to existing snapshots
      const limitedSnapshot = {
        ...snapshot,
        docs: docs.slice(0, LIMIT_LIVE)
      } as QuerySnapshot<DocumentData, DocumentData>
      this.assessmentsSnapshots.push(limitedSnapshot)
      this.isLoadingMore = false
    },
    retryAssessment: async function (assessment: Assessment) {
      this.isLoading = true
      const ref = assessment.toDocumentReference()
      if (ref)
        await updateDoc(ref, {
          status: "running",
          updated_at: serverTimestamp(),
        })
      this.isLoading = false
    },
    /**
     * True once a STOP command has been enqueued for this assessment and until it
     * actually leaves running/scheduled. Stop is asynchronous — the command only
     * *requests* the teardown — so an in-flight-request flag would re-enable the
     * button the moment `addDoc` resolves and let a second command be written.
     */
    isStopping(assessmentId: string | null | undefined): boolean {
      return !!assessmentId && this.stoppingIds.has(assessmentId)
    },
    /**
     * Request that a running/scheduled assessment be stopped by writing an
     * `assessment_run` STOP command (MYAC-775, command-queue pattern). The
     * backend's `assessment_run` executor cancels the Cloud Run execution,
     * cascades in-flight jobs, and moves the assessment to STOPPED. Replaces the
     * former `stop_requested` field-flip; the assessment doc is left untouched.
     *
     * The assessment id is held in `stoppingIds` from the moment the command is
     * enqueued until the assessment leaves running/scheduled, so the Stop control
     * stays disabled for the whole teardown instead of only for the write.
     */
    stopAssessment: async function (assessment: Assessment) {
      const ref = assessment.toDocumentReference()
      const pid = ref?.parent.parent?.id
      if (!ref || !pid || !assessment.id) return
      // Already requested — don't enqueue a duplicate command.
      if (this.stoppingIds.has(assessment.id)) return
      const { useAppStore } = await import('./app')
      const aid = assessment.id
      this.stoppingIds = new Set(this.stoppingIds).add(aid)
      try {
        const firestore = useFirestore()
        const commandsRef = collection(
          firestore,
          `projects/${pid}/command_queues/assessment_run/commands`
        )
        await addDoc(commandsRef, {
          status: 'requested',
          payload: {
            kind: 'assessment_run',
            assessment_id: aid,
            action: 'stop',
          },
          requested_by: null,
        })
        try { useAnalyticsStore().trackEvent('assessment_stop', { pid, aid }) } catch { }
        useAppStore().toast(t('assessments.stop.requested-title'), t('assessments.stop.requested-detail'), 'info')
      } catch (error) {
        // The command never landed — release the guard so the user can retry.
        const next = new Set(this.stoppingIds)
        next.delete(aid)
        this.stoppingIds = next
        logger.error('Failed to request assessment stop', error)
        useAppStore().toast(t('assessments.stop.error-title'), t('assessments.stop.error-detail'), 'error')
      }
    },
    /**
     * Release the stop guard for assessments **observed** to have reached a
     * terminal status (STOPPED/DONE/FAILED) — the teardown finished, so a future
     * run can be stopped again.
     *
     * Only ever releases on a terminal status we can see. Callers pass a partial
     * view of the project's assessments (a single doc, or the LIMIT_LIVE-capped
     * list), so an id missing from `assessments` says nothing about its run —
     * treating absence as "finished" would drop the guard mid-teardown and
     * re-open the duplicate-STOP window this guard exists to close. For the same
     * reason this checks for terminal values explicitly rather than "anything
     * that isn't running": an absent or unknown status is not evidence the run
     * ended, and holding the guard is the safe direction.
     *
     * Called from every surface that observes assessment state: the QMS
     * assessment/list snapshots here, and the TD dashboard components, which
     * subscribe to the running assessment doc themselves rather than through
     * this store.
     */
    reconcileStoppingIds(assessments: Assessment[]) {
      if (!this.stoppingIds.size) return
      let changed = false
      const next = new Set(this.stoppingIds)
      for (const a of assessments) {
        const id = a?.id
        if (!id || !next.has(id)) continue
        if (TERMINAL_ASSESSMENT_STATUSES.has(a.status)) {
          next.delete(id)
          changed = true
        }
      }
      if (changed) this.stoppingIds = next
    },
    /**
     * Release the stop guard for one assessment that a caller knows is no longer
     * the active run — used by the TD dashboard when `running_assessment_id` is
     * cleared on the TD current doc.
     *
     * That clearing is the backend positively saying "this run is no longer
     * active", which is why it's safe here and mere absence from a list is not.
     * It also covers a delivery race: the TD doc and the assessment doc have
     * separate listeners, and if the TD doc lands first the assessment listener
     * is torn down before it ever observes the terminal status.
     */
    releaseStoppingId(assessmentId: string | null | undefined) {
      if (!assessmentId || !this.stoppingIds.has(assessmentId)) return
      const next = new Set(this.stoppingIds)
      next.delete(assessmentId)
      this.stoppingIds = next
    },
    getTasksForResult: async function (pid: string, aid: string, resultId: string): Promise<Task[]> {
      const firestore = useFirestore()
      const tasksCollection = collection(firestore, `projects/${pid}/assessments/${aid}/results/${resultId}/tasks`)
      const snapshot = await getDocs(tasksCollection)
      return snapshot.docs.map((d) => Task.fromFirebase(d)).filter((t): t is Task => t !== null)
    },
    getQuestionsForTopic: async function (topic_ref: DocRef): Promise<Question[]> {
      const firestore = useFirestore()
      const questionsCollection = collection(firestore, `${topic_ref.path}/questions`)
      const snapshot = await getDocs(questionsCollection)
      return snapshot.docs.map((d) => Question.fromFirebase(d)).filter((q): q is Question => q !== null)
    },
    subscribeTaskGrouping: function (pid: string, aid: string) {
      if (
        this._task_grouping_unsub
        && this._task_grouping_pid === pid
        && this._task_grouping_aid === aid
      ) {
        return
      }
      this.unsubscribeTaskGrouping()

      if (!pid || !aid) return

      this._task_grouping_pid = pid
      this._task_grouping_aid = aid
      const firestore = useFirestore()
      const q = query(
        collection(firestore, `projects/${pid}/assessments/${aid}/task_grouping`),
        orderBy('order', 'asc'),
      )
      this._task_grouping_unsub = onSnapshot(
        q,
        (snapshot) => {
          this.taskGroupingSubjects = snapshot.docs
            .map((d) => TaskGroupingSubject.fromFirebase(d))
            .filter((s): s is TaskGroupingSubject => s !== null)
        },
        (error) => {
          logger.error('Error in task_grouping subscription', error)
        },
      )
    },
    unsubscribeTaskGrouping: function () {
      if (this._task_grouping_unsub) {
        this._task_grouping_unsub()
        this._task_grouping_unsub = null
      }
      this._task_grouping_pid = ''
      this._task_grouping_aid = ''
      this.taskGroupingSubjects = []
    },
    /**
     * Answer the keep-or-reset prompt, or dismiss it (§5.4).
     *
     * `null` cancels: the task stays parked, which is the state the user was
     * already in, so cancelling costs nothing and needs no undo.
     */
    /**
     * Offer a revival, or QUEUE it behind one already on screen (4.8a).
     *
     * A plain assignment was a last-write-wins race the moment more than one
     * parked row was accepted at once — see `reapproveQueue`. Deduped by task id
     * so a repeated sweep cannot ask twice about the same row.
     *
     * Resolves once the user's answer for THIS task has played out (MYAC-1557);
     * a repeated ask joins the first one's answer. Callers that only raise the
     * prompt can ignore it.
     */
    queueReapprove: function (prompt: NonNullable<typeof this.reapprovePrompt>): Promise<ReapproveOutcome> {
      const outcome = waitForReapprove(prompt.taskId)
      if (this.reapprovePrompt?.taskId === prompt.taskId) return outcome
      if (this.reapproveQueue.some((p) => p.taskId === prompt.taskId)) return outcome
      if (this.reapprovePrompt) this.reapproveQueue.push(prompt)
      else this.reapprovePrompt = prompt
      return outcome
    },
    /**
     * Revive every task the answered prompt covers — the one on screen, then the
     * batch behind it, all with the SAME `content_mode`.
     *
     * Failures are counted, not thrown: a sweep that revives eight of ten has
     * done eight real things, and aborting on the first refusal would leave the
     * user unable to tell which. One toast names the count.
     */
    _reviveBatch: async function (
      batch: Array<NonNullable<typeof this.reapprovePrompt>>,
      mode: ContentMode,
    ): Promise<number> {
      let failed = 0
      for (const item of batch) {
        try {
          const outcome = await submitPendingCommand(
            item.projectId,
            { kind: 'task_reapprove', task_id: item.taskId, content_mode: mode },
            item.baseSeq,
          )
          if (outcome.status === 'done' && item.taskPath) {
            // The recount the single-row path does, which this one did not.
            // Only an action-plan revival has an empty `taskPath` (nothing to
            // recount — no proposal behind the row); a KANBAN revival carries
            // one and needs it. Reachable because the queue is cross-surface
            // and store state outlives the route: a Kanban prompt with an
            // action-plan accept queued behind it makes `batch.length > 1`, and
            // every item then came down here instead, leaving the assessment's
            // aggregates stale with nothing on screen to say so.
            await this.recalculateAggregateReviewCountsFromTaskPath(item.taskPath)
          }
          if (outcome.status === 'done' || outcome.status === 'unresolved') {
            settleReapprove(item.taskId, 'revived')
          } else {
            settleReapprove(item.taskId, 'failed')
            failed += 1
            // LOG only, deliberately. `reportOutcome` toasts for every
            // notify-class refusal, so calling it here put one toast per failed
            // row on screen AND the aggregate below — four messages for three
            // failures, which is what the aggregate exists to avoid. The apply
            // layer's own explanation still reaches the log, where it is useful;
            // the user gets one line with the count.
            logger.error(
              `task reapprove ${item.taskId}: refused (${outcome.errorCode ?? outcome.status})`,
            )
          }
        } catch (e) {
          failed += 1
          settleReapprove(item.taskId, 'failed')
          logger.error(`task reapprove ${item.taskId}: could not be submitted`, e)
        }
      }
      return failed
    },
    resolveReapprove: async function (mode: ContentMode | null): Promise<void> {
      const prompt = this.reapprovePrompt
      if (!prompt || !mode) {
        // Cancel means STOP, not "skip this one": the user dismissed a dialog
        // they did not ask for row by row, and offering the next nine in a row
        // would be a worse answer than the one they gave. The rows keep their
        // accept buttons, so nothing is lost — only not done.
        const dropped = [prompt, ...this.reapproveQueue]
        this.reapprovePrompt = null
        this.reapproveQueue = []
        for (const item of dropped) if (item) settleReapprove(item.taskId, 'cancelled')
        return
      }
      // The batch this one answer covers. Taken BEFORE the first submit, so a
      // row queued while the commands are in flight is not silently absorbed
      // into a choice its user never saw.
      const batch = [prompt, ...this.reapproveQueue]
      this.reapproveQueue = []
      if (batch.length > 1) {
        // The sweep case: one question, every parked row in the group. The
        // single-row path below keeps its `resetRefused` re-offer, which only
        // makes sense when there IS one row to re-offer.
        let failed = 0
        try {
          failed = await this._reviveBatch(batch, mode)
        } finally {
          this.reapprovePrompt = null
        }
        if (failed > 0) {
          const { useAppStore } = await import('./app')
          useAppStore().toast(
            t('common.error'),
            t('tasks.errors.command.reapprove_batch_partial', { count: failed }, failed),
            'warn',
          )
        }
        return
      }

      // What the single revival came to, for whoever waits on it.
      let settled: ReapproveOutcome = 'failed'
      try {

      // **The prompt is held until the command settles**, and only then
      // cleared. Nulling it first closed the dialog on click, which made every
      // in-flight affordance it carries — `loading`, the disabled buttons, the
      // non-dismissable header — dead code, and left ~3s with no feedback at
      // all. There is nowhere else for that feedback to appear: the task is
      // parked, so 4.2's filter has it off the board and there is no card to
      // carry a pending indicator, and #349 deliberately deferred row-level
      // indicators on the summary tabs, which is where accept is offered.
      //
      // "Click a button and nothing happens for three seconds" is a narrower
      // version of the failure this whole card exists to remove.
      let outcome
      try {
        outcome = await submitPendingCommand(
          prompt.projectId,
          {
            kind: 'task_reapprove',
            task_id: prompt.taskId,
            content_mode: mode,
          },
          prompt.baseSeq,
        )
      } finally {
        this.reapprovePrompt = null
      }

      if (outcome.status === 'done' || outcome.status === 'unresolved') settled = 'revived'
      if (outcome.status === 'done') {
        // Skipped for an action-plan revival, which has no proposal behind it —
        // see `taskPath`. The revival itself has already applied; there is simply
        // nothing to recount.
        if (prompt.taskPath) {
          await this.recalculateAggregateReviewCountsFromTaskPath(prompt.taskPath)
        }
        return
      }
      if (outcome.status === 'unresolved') {
        // Not a failure, and deliberately not thrown — the command may still
        // apply. Same handling as un-approve's.
        logger.warn(`task reapprove ${prompt.taskId}: unresolved - aggregates not recounted`)
        return
      }

      if (mode === 'reset' && outcome.errorCode === 'policy') {
        // The one refusal this card creates the button for, and the one whose
        // message names its own remedy. Re-opening with `keep` the only option
        // turns "That change is not allowed on this task" into a choice the
        // user can actually make.
        //
        // Deliberately narrow: `policy` also covers refusals that have nothing
        // to do with content mode, and `useTaskCommandErrors` already records
        // that splitting the code is a spec change tracked elsewhere. Pairing
        // it with `reset` is what keeps this from swallowing those.
        //
        // It still ASSUMES the refusal was about content mode, because `policy`
        // cannot confirm it. A lifecycle refusal is the case that breaks the
        // assumption — reachable now that unknown lifecycles route here by
        // design — and there the user picks Reset, is told only Keep remains,
        // picks Keep, and is refused again with the generic message. Self
        // terminating, and it needs the server to have added a lifecycle this
        // build has never seen, so it is left rather than built around.
        this.reapprovePrompt = { ...prompt, resetRefused: true }
      }

      const verdict = reportOutcome(outcome)
      if (verdict.action === 'conflict') {
        const { useAppStore } = await import('./app')
        useAppStore().toast(
          t('tasks.errors.command.stale_title'),
          t('tasks.errors.command.stale_operation'),
          'warn',
        )
      }
      // Same reasoning as the un-approve throw below: the class alone does not
      // say why, and this message is what a caller's `catch` prints.
      throw new CommandRefused(
        `task_reapprove refused: ${outcome.errorCode ?? outcome.status}`
        + (outcome.error ? ` - ${outcome.error}` : ''),
      )
      } finally {
        // A `reset` refused re-offers the SAME row with `keep` only: the user is
        // still answering, so whoever waits keeps waiting.
        if (this.reapprovePrompt?.taskId !== prompt.taskId) settleReapprove(prompt.taskId, settled)
        // Offer the next queued revival — but only if nothing re-opened this
        // one. The `resetRefused` branch above deliberately puts the SAME row
        // back on screen with `keep` as its only option, and advancing over that
        // would drop the user out of a choice they are still making.
        if (!this.reapprovePrompt) {
          this.reapprovePrompt = this.reapproveQueue.shift() ?? null
        }
      }
    },
    /**
     * The Kanban task behind a path, read from Firestore rather than the board.
     *
     * One read, two callers, each applying its own test — send-back asks
     * whether the task is on the board and has a proposal, accept asks whether
     * it is parked. Sharing the read rather than the *decision* is deliberate:
     * the two questions have opposite answers for the same document, and
     * MYAC-1003's review is where trying to share the decision went wrong.
     */
    _projectTaskAt: async function (
      candidate: { projectId: string; taskId: string },
    ): Promise<ProjectTask | null> {
      const snapshot = await getDoc(
        doc(useFirestore(), `projects/${candidate.projectId}/tasks/${candidate.taskId}`),
      )
      // `fromFirebase` answers existence and content at once: null for a
      // document that is not there, a parsed task otherwise.
      return ProjectTask.fromFirebase(snapshot)
    },
    /**
     * The task a send-back of `taskPath` would target, or `null` if there is
     * none to send back (IR-126 §4.3, phase 4.2a).
     *
     * **Existence is necessary and not sufficient — the task must also be on
     * the board.** `task_unapprove` is permitted only from `ACTIVE`
     * (`apply.py`'s `PERMITTED_LIFECYCLES`), so routing an already-parked task
     * into it produces a `POLICY` refusal, a `CommandRefused` throw, and a
     * dialog with no way forward. That is reachable in one ordinary step:
     * un-approve a task, and its proposal goes back to `pending` — rejecting
     * that pending proposal is the obvious next action, and it would find the
     * task sitting in `unapproved`.
     *
     * Falling through to the caller's batch for a parked task is the right
     * answer, and it is right **only because this card removed the batch's
     * `batch.delete`**: what remains is the proposal's `review_decision`, which
     * is exactly what rejecting an already-parked proposal should write. The
     * two halves of this change depend on each other.
     *
     * `isOnBoard` rather than a fresh lifecycle comparison, because it is the
     * same predicate 4.2 already wrote and tested, and its permissiveness is
     * deliberate: a lifecycle this build has never seen routes to the command
     * and lets the apply layer — the authority — decide, rather than being
     * silently downgraded to a proposal-only write here.
     *
     * That case does end in a refusal — `TaskLifecycle` degrades to `UNKNOWN`,
     * which is in no permitted set — and the user sees the generic `POLICY`
     * text rather than "this client is out of date", which is what actually
     * happened. Accepted rather than fixed: it is reachable only if the server
     * ships a lifecycle ahead of the front, and the alternative is writing to
     * the proposal while its task sits in a state we cannot read.
     *
     * **Existence decides this, not the shape of the path.** Un-approve used to
     * be reachable only from a path shaped `projects/{pid}/tasks/{tid}`, and
     * two populations never present that shape:
     *
     *   - **legacy-format tasks** (`!name && task_ref`), which the board
     *     resolves through `_subscribeToSourceTask` from the SOURCE snapshot
     *     with no path override — compare `_resolveFromProjectTask`, which
     *     `defineProperty`s `path` to the Kanban path — so `task.path` is the
     *     assessment path
     *   - **accepted tasks whose proposal has no `project_task_ref`**, which
     *     `isAcceptedKanbanTask` therefore refuses to overlay, so the
     *     assessment screens hand over the source path
     *
     * Neither reached the command. Both fell through to `updateTaskReview`'s
     * batch and were hard-deleted, writing no revision — the behaviour 4.2
     * exists to reverse, reached by a route 4.2 did not close.
     *
     * **The two shapes carry different amounts of proof, and this respects
     * that.** A Kanban path IS the task's own path, so holding one already
     * proves the task exists; a proposal path proves nothing, since it reads
     * identically whether or not it was ever accepted. So only the second
     * shape has to establish *existence*.
     *
     * **But existence is not the only question, and that is what a first
     * attempt got wrong.** Whether the task has a proposal at all is
     * independent of which path the caller holds — and a manual task's path is
     * *always* Kanban-shaped, because `_resolveFromProjectTask` sets it that
     * way and a manual task appears nowhere else. A manual check behind the
     * Kanban short-circuit is therefore dead code for the only case it exists
     * to catch. Both shapes read the document; they differ in what they do
     * with it.
     *
     * **And it asks Firestore, not the board store.** The board's subscription
     * is the tempting source — already open, already holding every task — but
     * on the assessment surfaces this exists for, `loadKanbanTasks` is fired
     * unawaited, so "not loaded yet" and "no such task" are one answer.
     * Guessing wrong is not a no-op: the caller's fall-through still commits
     * the proposal write, so a reject would mark the proposal rejected while
     * its task stayed active on the board — no revision, nothing to retry, and
     * visible only under load. That divergence is worse than the hard-delete
     * this card removed.
     *
     * The task inherits its proposal's id (§7.3), so both shapes yield the same
     * id. The parse still happens; it just no longer decides *whether* to send
     * a command, only where to read the id from. `proposalTarget` returns null
     * for a Kanban path by design, which is why this is two extractors.
     *
     * **A task with no proposal takes the command, not the batch** — and the
     * distinction outlived the third outcome that used to enforce it
     * (MYAC-1098). While `task_unapprove` refused such a task there was no
     * command to take, so `nothing` existed to stop the only remaining
     * fall-through: a `batch.update` writing `review_decision` onto a task
     * nobody proposed — meaningless, outside the ledger, and the card would
     * have rendered as rejected on a decision nothing recorded.
     *
     * The refusal is gone, so the command IS the answer and `nothing` was
     * removed with it. What has not changed is the batch: a decision still
     * goes through the ledger, never around it.
     *
     * A parked task on a *proposal* path is different: there the fall-through
     * writes the proposal's own decision, which is exactly what rejecting an
     * already-parked proposal should do. Fall through only where falling
     * through means something.
     *
     * The read is not transactional, so the task can change between it and the
     * write. Benign in both directions now that the delete is gone: a task
     * parked after the read gets a command the apply layer refuses, and one
     * revived after the read gets a proposal write with no command — neither
     * destroys anything, and both are repeatable by the user.
     */
    _sendBackRoute: async function (taskPath: string): Promise<SendBackRoute> {
      const kanbanMatch = taskPath.match(/^projects\/([^/]+)\/tasks\/([^/]+)$/)
      const candidate = kanbanMatch
        ? { projectId: kanbanMatch[1], taskId: kanbanMatch[2] }
        : proposalTarget(taskPath)
      if (!candidate) return { via: 'batch' }

      const task = await this._projectTaskAt(candidate)

      // A manual task used to be short-circuited here, because the apply layer
      // refused it and archive was its exit (§4.3). MYAC-1098 removed both, and
      // this guard outlived them by one deploy: it kept the command from ever
      // being written, so Reject on a hand-made task did nothing at all — no
      // decision, no revision, no column change, and the button still there to
      // press again.
      //
      // Nothing replaces it. A manual task's path is Kanban-shaped
      // (`_resolveFromProjectTask` sets it that way), so it falls into the
      // branch below and earns a real command like every other task.
      if (kanbanMatch) {
        // Existence is already proven. Lifecycle deliberately is NOT re-checked
        // here: a card the board is showing may have been parked by a teammate
        // since, and sending the command earns an honest refusal, where falling
        // through would write a decision onto a parked task with no revision.
        return { via: 'command', ...candidate }
      }

      // EXISTS. That is the whole test (MYAC-1095).
      //
      // The rule, from the product owner (Sy, 2026-08-20): *if a proposal is
      // linked to a task, every user action points to the task; with no
      // reference, the proposal's own endpoints stand.* The REFERENCE decides —
      // not the gesture, not the lifecycle, and not what the screen happens to
      // be rendering.
      //
      // This read `task?.isOnBoard`, which excludes a PARKED task, and the
      // reason was real at the time: `task_unapprove` was permitted only from
      // ACTIVE, so routing a parked task into it earned a POLICY refusal with
      // no way forward. CMS `1316129b` widened that cell to
      // `{active, unapproved}` precisely so this step would work.
      //
      // Falling through to the batch was the wrong answer once a task existed:
      // it wrote `review_decision` onto the PROPOSAL alone, so rejecting a
      // parked proposal recorded nothing on the task — no ruling, no reason, no
      // revision — and the two documents then disagreed about the same
      // decision. The command records both (this arm's proposal write-back is
      // not skipped) and appends a revision, so one gesture leaves one story.
      //
      // `archived` no longer carves itself out, and dropping that is the point
      // rather than a tidy-up. It is in no permitted set, so the command IS
      // refused — but a refusal the user can see beats a proposal write they
      // cannot: the screen keeps reading the archived task, so the batch's
      // decision would land where nothing displays it, exactly the silent loss
      // this card exists to end. The apply layer is the authority on what it
      // accepts; nothing here should be predicting its answer.
      //
      // `requirementsAssessment.ts`'s `editRemediation` gate is now the same
      // sentence about the same fact. One rule, both arms.
      return task ? { via: 'command', ...candidate } : { via: 'batch' }
    },
    /**
     * Send an accepted task back to its proposal, as a command (§4.3, phase 4.2).
     *
     * **It reverses what this used to do.** The batch deleted the Kanban task
     * and flipped the proposal; `task_unapprove` keeps the task, sets
     * `lifecycle: unapproved`, and appends a revision — so the trail survives
     * the round trip instead of being stranded by it. The board stops showing
     * the task because `ProjectTask.isOnBoard` is false, not because the
     * document vanished.
     *
     * **Two user actions arrive here, and the difference is the `decision`.**
     * Un-approving sends a task back to `pending`; rejecting an
     * already-accepted task sends it back as `rejected` with a reason. Both
     * reach this function today because an accepted task's `path` is its Kanban
     * copy, so every reject dialog lands here once the task is on the board.
     * MYAC-999 added the two fields the command needed to tell them apart.
     *
     * Unlike `task_create`, this carries a **`base_seq`** — the task exists, so
     * there is a revision to be stale against. It therefore goes through the
     * sequenced path and can come back `stale`, which a create structurally
     * could not, and `submitPendingCommand` shows the card as saving meanwhile.
     */
    _unacceptKanbanTask: async function (
      pid: string,
      taskId: string,
      /**
       * Whatever path the caller held — the Kanban copy, or (since MYAC-1003)
       * the proposal itself, for a task the assessment screens never overlaid.
       */
      callerPath: string,
      payload: { review_decision?: TaskReviewDecision; review_rejection_comment?: string | null },
      /**
       * Suppress this call's OWN failure toast (MYAC-1061). The refusal is
       * still classified, still logged and still THROWN — only the per-item
       * message is withheld, so a bulk caller can report `n succeeded, m did
       * not` once instead of firing one toast per refusal on top of its own
       * summary. Default `false`: a single reject keeps reporting itself, and
       * silence is opt-in by the one caller that owns a better message.
       */
      opts: { quiet?: boolean } = {},
    ) {
      // Only for the recount below — the apply layer does its own resolution
      // from `proposal_locator` and ignores anything decided here.
      //
      // **A caller holding the proposal path has already answered this.**
      // Re-deriving it would be strictly worse than using it: a legacy QMS task
      // has no `source_assessment_ref` to resolve from, so
      // `resolveUnacceptSourcePath` would fall through to its `td_latest`
      // default and recount the wrong assessment — silently, since a recount
      // that finds nothing looks the same as one with nothing to do.
      // `proposalTarget`, not a fresh path test. It is the helper #352
      // introduced so that another copy would not be the easier option, and its
      // own docstring warns that "an edit to one of four is how that stops
      // being true" — this would have been the fifth. It is also the stronger
      // test: `includes('/assessments/')` matches a substring anywhere, where
      // this anchors the whole path.
      const kanbanTask = this.allTasks.find((t) => t.path === callerPath)
        || useTDCurrentStore().allTasks.find((t: Task) => t.path === callerPath)
      const sourcePath = proposalTarget(callerPath)
        ? callerPath
        : resolveUnacceptSourcePath(kanbanTask?.source_assessment_ref, pid, taskId)

      const rejecting = payload.review_decision === 'rejected'
      // `TaskReviewDecision` also has `directive_rejected`, and the command's
      // `decision` is `pending | rejected` — so it maps to `pending`, which is
      // the only honest option but is a real narrowing. The old batch wrote the
      // value verbatim.
      //
      // No caller sends it today: an auto-rejected proposal has no task, so it
      // never reaches an un-accept. Said out loud anyway, for the same reason
      // `_acceptProposal` warns about the fields it drops — "no caller does
      // this" is a fact about the callers, and a warn makes it a property of
      // the code.
      if (payload.review_decision && payload.review_decision !== 'pending' && !rejecting) {
        logger.warn(
          `un-approve of ${taskId}: ${payload.review_decision} sent back as pending - `
          + 'task_unapprove carries only pending | rejected',
        )
      }
      // `base_seq` is deliberately absent from this object — `SequencedPayload`
      // omits it and the ledger composes it from the `serverSeq` argument, so
      // that the number a command claims and the number the ledger reserved
      // cannot disagree.
      const outcome = await submitPendingCommand(
        pid,
        {
          kind: 'task_unapprove',
          task_id: taskId,
          decision: rejecting ? 'rejected' : 'pending',
          // Sent only when rejecting. The server REFUSES a comment alongside a
          // send-back rather than ignoring it, so passing one unconditionally
          // would turn every un-approve into a policy failure.
          ...(rejecting && payload.review_rejection_comment
            ? { review_rejection_comment: payload.review_rejection_comment }
            : {}),
        },
        useTasksStore().revisionSeqFor(taskId),
      )

      if (outcome.status === 'done') {
        // Only once the task actually moved. Recounting against a state that
        // has not changed would write a number the board contradicts a second
        // later.
        await this.recalculateAggregateReviewCountsFromTaskPath(sourcePath)
        return
      }

      if (outcome.status === 'unresolved') {
        // Not a failure, and deliberately not thrown. The command may still
        // apply; `submitPendingCommand` has already left an `unconfirmed`
        // marker on the card, which is the honest statement — the task is
        // still on the board and we cannot say whether it will leave. Throwing
        // would hold the dialog open on a change that probably worked.
        logger.warn(`task unapprove ${taskId}: unresolved - aggregates not recounted`)
        return
      }

      // **Refused. This has to be both said and thrown.**
      //
      // Said, because the old batch could only commit or throw, and a refusal
      // now resolves normally — so without this the task silently stays on the
      // board while every surface behaves as though it left. That is the
      // vanished-edit failure this phase exists to remove, reintroduced by the
      // migration meant to remove it.
      //
      // Thrown, because callers depend on it: `onRejectConfirm` in SummaryTab,
      // TDSummaryTab, TaskDetailModal, TaskDetailDialog and SingleTask all wrap
      // this in `try/catch` and close the dialog only on the success path. A
      // resolved-but-refused outcome closes the dialog and clears the pending
      // reject as if it had worked.
      //
      // `reportOutcome` owns the message for every class except one.
      const verdict = reportOutcome(outcome, { quiet: opts.quiet })

      // **`stale` is the exception, and it needs its own answer here.**
      //
      // `reportOutcome` deliberately raises no toast for a conflict, because
      // the field-edit path opens `TaskConflictDialog` instead — a dialog that
      // shows the stored value against the attempted one and lets the user
      // pick. That mechanism does not fit this operation: `TaskConflict` is
      // `{ stored, attempted }` and an un-approve has no field and no
      // attempted VALUE. Filling it in would mean inventing a pair, and asking
      // someone to choose between two values neither of which is what happened.
      //
      // The throw alone is not enough either — all five callers `catch {}`, so
      // a stale refusal would stop the spinner and say nothing. Which is how
      // this survived the first fix: the comment there noted the toast was
      // absent and treated the throw as sufficient.
      //
      // Retrying is safe on this operation, unlike an edit: re-issuing an
      // un-approve after seeing the current state cannot overwrite a change we
      // could not see, because it carries no value of its own.
      if (verdict.action === 'conflict' && !opts.quiet) {
        const { useAppStore } = await import('./app')
        useAppStore().toast(
          // Neutral on purpose. `conflict_title` reads "…while you were
          // editing", which is the field-edit path's framing — nobody edited
          // anything here, they sent a task back. `stale_operation` was
          // written to avoid exactly that wording, so pairing it with that
          // title would undo half of why it exists.
          t('tasks.errors.command.stale_title'),
          t('tasks.errors.command.stale_operation'),
          'warn',
        )
      }

      // The apply layer's own sentence rides along, not just the class: this
      // message is what a caller's `catch` logs and what a bug report pastes,
      // and `policy` alone cannot tell "the proposal no longer exists" from
      // "this task never had one".
      //
      // It has to be HERE rather than in `reportOutcome`, which already carries
      // the same detail: that line is `logger.info`, deliberately — a warning
      // per ordinary refusal would bury the ones that mean something — and
      // `info` is compiled to a no-op in every built bundle. So on a deployed
      // environment the reason existed nowhere until this throw carried it
      // (reported on dev, 2026-08-18: a task that could be edited but not sent
      // back, with `policy` as the entire explanation).
      throw new CommandRefused(
        `task_unapprove refused: ${outcome.errorCode ?? outcome.status}`
        + (outcome.error ? ` - ${outcome.error}` : ''),
      )
    },
    /**
     * Approve one proposal through `task_create`, then run what used to follow
     * `batch.commit()` (IR-126 §4.3, phase 4.1).
     *
     * The four side effects are **not** passed as `onCreated`, even though that
     * is the seam built for them. `onCreated` fires inside `submitTaskCreate`
     * and cannot be awaited from here, and callers await this action — so
     * hanging the recount off it would resolve the action before the aggregate
     * counts moved. Capturing the path there and acting on the resolved boolean
     * keeps the await and still uses the authoritative path the apply layer
     * returned, rather than a reconstructed one.
     *
     * Nothing runs when the command does not resolve. That is deliberate and it
     * costs something real — an `unresolved` create may still apply, leaving a
     * task on the board that TraceX was never told about. `submitTaskCreate`
     * logs it; reconciling it needs a watcher for a task we stopped waiting
     * for, which is 4.6/4.7's problem rather than this one's.
     */
    _acceptProposal: async function (
      taskPath: string,
      target: { projectId: string; taskId: string },
      payload: Record<string, unknown> = {},
    ): Promise<void> {
      // `updateTaskReview`'s signature still advertises `review_assignee_uid`,
      // `review_progress`, `directive_id` and `directive_match_reason`, and the
      // old batch wrote all of them onto the proposal. A create carries none:
      // `TaskCreateCommand` has nowhere to put them, and `apply_create` writes
      // only `review_decision`, `project_task_ref` and `updated_at`.
      //
      // Every accept call site sends `{ review_decision }` alone today, so
      // nothing is dropped. This exists so that stops being a fact about the
      // callers and becomes something the code says out loud — a silent drop
      // behind an unchanged signature is the exact failure this phase keeps
      // finding, and the next caller to add a field would get no signal at all.
      const carried = Object.keys(payload).filter((k) => k !== 'review_decision')
      if (carried.length > 0) {
        logger.warn(
          `accept of ${taskPath} dropped ${carried.join(', ')} - task_create carries only the decision`,
        )
      }
      // Read BEFORE the write. `directive_rejected` is what the proposal says
      // right now; once the command applies, `apply_create` has overwritten it
      // with `accepted` and the fact that this accept overrode an auto-rejection
      // is unrecoverable.
      const tdStore = useTDCurrentStore()
      const sourceTask =
        this.allTasks.find((t) => t.path === taskPath)
        || tdStore.allTasks.find((t: Task) => t.path === taskPath)
      const overrodeDirective = sourceTask?.review_decision === 'directive_rejected'

      let createdPath: string | null = null
      const created = await submitTaskCreate({
        proposalPath: taskPath,
        taskId: target.taskId,
        projectId: target.projectId,
        onCreated: (path) => {
          createdPath = path
        },
      })
      if (!created) return

      // `onCreated` fires on every path that returns true, so this fallback
      // should be unreachable. It is a reconstruction rather than a `return`
      // because the alternative is silence: skipping the export leaves a task
      // on the board that TraceX was never told about, with nothing anywhere
      // saying why. The reconstructed path is the same one `submitTaskCreate`
      // falls back to internally, and §7.3 guarantees the id.
      const taskDocPath: string =
        createdPath ?? `projects/${target.projectId}/tasks/${target.taskId}`

      await this.recalculateAggregateReviewCountsFromTaskPath(taskPath)

      // Auto-export TraceX (no-op when the project has none configured).
      const { useTracexExportStore } = await import('./tracexExport')
      void useTracexExportStore().autoExportOnAccept(target.projectId, taskDocPath)

      // CSM: the user overrode an auto-rejection. The count is unchanged — the
      // directive itself stays active.
      if (overrodeDirective) {
        try {
          const active = useTaskDirectivesStore().directives.filter((d) => d.is_active).length
          trackAction(CsmAction.DirectiveOverridden, {
            projectId: target.projectId,
            projectMeta: { directive_count: active },
          })
        } catch { /* best-effort */ }
      }

      try {
        trackAction(CsmAction.TaskAccepted, { projectId: target.projectId })
      } catch { /* best-effort */ }
    },
    /**
     * Set aside a Product's proposal that has no task, through `review_td_task`
     * (MYAC-1660), then run what used to follow the batch: the aggregate recount
     * and the CSM event. The section recount is the executor's own.
     *
     * Throws on a refusal, as the batch threw on a failed commit, because every
     * reject dialog closes only on the success path. Two outcomes do not throw:
     * a proposal already set aside (by the chat, a teammate or a directive) is
     * the state the user asked for, and an unanswered command may still apply,
     * as `_unacceptKanbanTask` treats `unresolved`.
     */
    _setAsideTDProposal: async function (
      target: ProposalTarget,
      taskPath: string,
      payload: { review_decision?: TaskReviewDecision; review_rejection_comment?: string | null },
      /** See `_unacceptKanbanTask`. Only the toast is suppressed. */
      opts: { quiet?: boolean } = {},
    ): Promise<void> {
      // Every reject call site sends the decision and the comment alone today; a
      // field added later would be dropped by the strict command, so say it.
      const carried = Object.keys(payload)
        .filter((k) => k !== 'review_decision' && k !== 'review_rejection_comment')
      if (carried.length > 0) {
        logger.warn(`set-aside of ${taskPath} dropped ${carried.join(', ')} - review_td_task carries the comment only`)
      }

      const outcome = await submitTDTaskReview(
        target.projectId, target.taskId, payload.review_rejection_comment ?? null,
      )

      if (outcome.status === 'done') {
        await this.recalculateAggregateReviewCountsFromTaskPath(taskPath)
        try { trackAction(CsmAction.TaskRejected, { projectId: target.projectId }) } catch { /* best-effort */ }
        return
      }
      if (outcome.status === 'unresolved') {
        logger.warn(`review_td_task ${target.taskId}: unresolved - aggregates not recounted`)
        return
      }

      const code = outcome.error ?? ''
      const { useAppStore } = await import('./app')
      if (code === 'already_decided:rejected' || code === 'already_decided:directive_rejected') {
        if (!opts.quiet) {
          useAppStore().toast(
            t('tasks.errors.command.already_set_aside_title'),
            t('tasks.errors.command.already_set_aside'),
            'info',
          )
        }
        return
      }

      if (!opts.quiet) {
        if (code === 'conflict') {
          useAppStore().toast(
            t('tasks.errors.command.stale_title'), t('tasks.errors.command.stale_operation'), 'warn',
          )
        } else if (code.startsWith('already_decided:') || code === 'owned_by_task') {
          useAppStore().toast(
            t('tasks.errors.command.stale_title'), t('tasks.errors.command.decided_meanwhile'), 'warn',
          )
        } else if (code === 'unknown_task') {
          useAppStore().toast(t('common.error'), t('tasks.errors.command.task_not_found'), 'error')
        } else {
          useAppStore().toast(t('common.error'), t('tasks.errors.command.generic'), 'error')
        }
      }
      throw new CommandRefused(`review_td_task refused: ${code || outcome.status}`)
    },
    updateTaskReview: async function (
      taskPath: string,
      payload: Partial<{
        review_decision: TaskReviewDecision
        review_assignee_uid: string | null
        review_progress: TaskProgressStatus
        review_rejection_comment: string | null
        directive_id: string | null
        directive_match_reason: string | null
      }>,
      /** See `_unacceptKanbanTask`. Only the per-item toast is suppressed. */
      opts: { quiet?: boolean } = {},
    ) {
      try {
        const firestore = useFirestore()

        // IR-126 §4.3 / MYAC-1003 — a send-back goes to `task_unapprove`
        // whenever there is a task to send back, whatever path the caller
        // holds. `_sendBackRoute` is where that question is answered, and
        // the gate is here so the read it may cost never happens on an accept
        // or a comment-only edit.
        const sendingBack = !!payload.review_decision && payload.review_decision !== 'accepted'
        if (sendingBack) {
          const route = await this._sendBackRoute(taskPath)
          if (route.via === 'command') {
            await this._unacceptKanbanTask(
              route.projectId, route.taskId, taskPath, payload, { quiet: opts.quiet },
            )
            return
          }
        }

        // MYAC-1660 — a Product's proposal with no task is set aside by the
        // `review_td_task` command the chat sends, never by the batch below: two
        // writers of one decision could overwrite each other. Only `rejected`
        // moves; back to pending and the other fields keep the batch.
        const tdProposal =
          payload.review_decision === 'rejected' ? tdLatestProposalTarget(taskPath) : null
        if (tdProposal) {
          await this._setAsideTDProposal(
            tdProposal, taskPath, payload, { quiet: opts.quiet },
          )
          return
        }

        // IR-126 §4.3 — approving a proposal is a command, not a batch. The
        // browser no longer builds the task; `apply_create` derives it from the
        // proposal inside one transaction and writes its `seq 0` revision with
        // it, so genesis is a derivation rather than a client claim.
        //
        // **Accept only.** A reject writes no task at all — it deletes one, and
        // the whole batch below still applies. Un-approve is 4.2's, and is
        // handled above. So this is the single branch that moves.
        const target =
          payload.review_decision === 'accepted' ? proposalTarget(taskPath) : null
        if (target) {
          // IR-126 §5.4 / MYAC-990 — accepting a task that already exists is a
          // REVIVAL, not a creation.
          //
          // Until 4.2 un-approve deleted the Kanban task, so re-approving was
          // just accepting the proposal again and `task_create` was right. 4.2
          // keeps the task: the create now hits `ALREADY_CREATED`, which means
          // "the task exists, which is what you wanted — converge on it", so
          // the client converges and says nothing. Un-approve a task, click
          // accept, and nothing happens.
          //
          // Observed on dev 2026-08-11 on two tasks, from both directions:
          // un-approve→accept, and reject-an-accepted-task→accept. The second
          // reads as "you cannot go from rejected to accepted", which is not a
          // rule — it is this, on a task that happens to have a parked copy.
          const existing = await this._projectTaskAt(target)
          if (existing && !existing.isSettled && !existing.isArchived) {
            // `content_mode` is required and has no default (§5.4): keeping the
            // edits and reverting them are both defensible, and choosing
            // silently would either discard a human's work or approve edits
            // they meant to drop. The user is asked — see `reapprovePrompt`.
            // Named, not spread: `proposalTarget` also returns `kind`, and a
            // prompt carrying a field it has no use for is a field the next
            // reader has to work out the relevance of.
            //
            // Through `queueReapprove`, NOT assigned. A plain assignment is
            // last-write-wins, and this is the surface where two parked
            // proposals can be accepted at once — the race `queueReapprove`
            // exists to close was left open on the one screen that had it
            // first. It also queues correctly ACROSS surfaces: an action-plan
            // accept arriving while this prompt is up now waits behind it
            // instead of replacing it.
            this.queueReapprove({
              taskPath,
              projectId: target.projectId,
              taskId: target.taskId,
              // From the DOCUMENT just read, not from the board store.
              // `revisionSeqFor` returns `?? 0` when the board has not loaded,
              // and `loadKanbanTasks` is fired unawaited from the assessment
              // stores this is reached from — so the anchor would not merely
              // be possibly stale, it would be GUARANTEED stale: a parked task
              // is at seq >= 1 by definition, since `apply_unapprove`
              // allocates `last_revision_seq + 1`. The user would be told the
              // task changed while they were working, about a task nobody
              // touched. `_projectTaskAt` exists to end exactly this
              // dependency; taking the number from the store would put it back.
              baseSeq: existing.revisionSeq,
            })
            return
          }
          if (existing) {
            // Only the two states this build RECOGNISES as nothing-to-do reach
            // here: `active` (the accept has already landed) and `archived`
            // (terminal — §5.7 puts it in no permitted set). A lifecycle the
            // server adds later does not, and must not: ignoring it would be
            // the click-does-nothing bug this card exists to fix, reproduced
            // for every future state. Those route to the command above and let
            // the apply layer answer.
            logger.info(
              `accept of ${taskPath} ignored: task is ${existing.isArchived ? 'archived' : 'already on the board'}`,
            )
            return
          }
          await this._acceptProposal(taskPath, target, payload as Record<string, unknown>)
          return
        }

        const taskRef = doc(firestore, taskPath)
        const batch = writeBatch(firestore)

        const updatePayload: Record<string, unknown> = { ...payload }
        if (payload.review_decision !== undefined) {
          if (payload.review_decision === 'rejected') {
            updatePayload.review_rejection_comment = payload.review_rejection_comment ?? null
          } else {
            updatePayload.review_rejection_comment = null
          }
        }

        batch.update(taskRef, {
          ...updatePayload,
          updated_at: serverTimestamp(),
        })

        // The Kanban delete is **gone**, and it had to go with the routing
        // change rather than after it (MYAC-1003).
        //
        // Reaching here with a send-back decision means `_sendBackRoute` said
        // `batch`, so there is either no task or a parked one — and a
        // parked task must survive, since keeping it is the whole point of
        // `task_unapprove`. Either way the delete was wrong.
        //
        // Leaving it would also be worse than untidy: `allow delete` on
        // `projects/{pid}/tasks/{tid}` closes in 4.5, and Firestore refuses a
        // delete on permission, not on existence. A batch carrying a no-op
        // delete would start failing the whole reject the day that rule lands,
        // for proposals that were never accepted at all.
        //
        // Its removal is also what makes the parked case correct rather than
        // merely survivable: what remains is the proposal's `review_decision`,
        // which is exactly what rejecting an already-parked proposal should do.

        logger.info('[updateTaskReview] committing batch...')
        await batch.commit()
        logger.info('[updateTaskReview] batch committed OK')
        await this.recalculateAggregateReviewCountsFromTaskPath(taskPath)

        // CSM: task rejected. The accepted counterpart moved to
        // `_acceptProposal`, which fires it once the task actually exists
        // rather than once the batch committed. A reject-with-directive
        // co-fires directive_created (its own method) — both are intentional.
        const reviewedPid = taskPath.split('/')[1] // taskPath = projects/{pid}/...
        if (reviewedPid && payload.review_decision === 'rejected') {
          try { trackAction(CsmAction.TaskRejected, { projectId: reviewedPid }) } catch { /* best-effort */ }
        }
      } catch (error) {
        logger.error('[updateTaskReview] FAILED', error)
        // A refused command has already said what went wrong, in the apply
        // layer's own terms — `stale` opens the conflict dialog, `policy` and
        // `unauthorized` get their own message. The generic toast on top of
        // that would be a second, vaguer answer to a question already
        // answered. Still rethrown, because callers close their dialogs only
        // on the success path.
        //
        // `quiet` has to be honoured HERE too, and this is the path that
        // matters most for it (review of #454). A bulk reject's common case is
        // proposals that were never accepted: `_sendBackRoute` answers `batch`,
        // so nothing reaches `_unacceptKanbanTask` and a failure here is a
        // Firestore error rather than a `CommandRefused`. Guarding only the
        // refusal branch left the toast-per-item bug fully intact for exactly
        // the scenario `quiet` was added to fix.
        if (!(error instanceof CommandRefused) && !opts.quiet) {
          const { useAppStore } = await import('./app')
          useAppStore().toast('Error', 'Failed to update task review', 'error')
        }
        throw error
      }
    },
    _tasksInSubject(subject: TaskGroupingSubject, allTasks: Task[]): Task[] {
      // Join subject refs (`results/{rid}/tasks/{tid}`) to tasks by task id, not
      // path: once accepted, a task is backed by its kanban copy whose path is
      // `projects/{pid}/tasks/{tid}`, so a path-suffix match would drop it.
      // (MYAC-719 × MYAC-708)
      const ids = new Set((subject.taskRefs ?? []).map(taskIdFromRef))
      return allTasks.filter((t) => !!t.id && ids.has(t.id))
    },
    _pendingTasksInSubject(subject: TaskGroupingSubject, allTasks: Task[]): Task[] {
      return this._tasksInSubject(subject, allTasks).filter((t) => t.effectiveDecision === 'pending')
    },
    /**
     * Approve every unaccepted proposal in a subject (IR-126 §4.3, phase 4.1).
     *
     * Was a chunked `writeBatch` building the same ~30-field task object as the
     * single-accept path. It is now N independent `task_create` commands, and
     * the difference is not only where the task is built: a batch-created task
     * carried **no revision at all**, so "accept all" produced tasks with no
     * trail while the same tasks accepted one at a time got a `seq 0` genesis.
     * One accept path, one genesis, regardless of which button made it.
     *
     * **Concurrent, not sequential.** Creates do not join the chain (§4.3) —
     * two approvals of different proposals are independent, and one failing
     * must not discard the rest. Sequential submission would also multiply the
     * command timeout by the subject size, so a twenty-task subject could spend
     * five minutes resolving.
     *
     * The aggregate side effects still run once, and only for what actually
     * landed: `submitTaskCreate` never throws, so a partial result is normal
     * rather than exceptional and the count reported to CSM is what succeeded.
     */
    acceptAllInSubject: async function (subject: TaskGroupingSubject, allTasks: Task[]): Promise<void> {
      try {
        const pendingTasks = this._tasksInSubject(subject, allTasks).filter((t) => t.effectiveDecision !== 'accepted')
        if (pendingTasks.length === 0) return

        const targets = pendingTasks
          .map((task) => ({ task, target: proposalTarget(task.path) }))
          .filter((t): t is { task: Task; target: NonNullable<ReturnType<typeof proposalTarget>> } => t.target !== null)
        if (targets.length === 0) return

        // Bounded, not unbounded. Sequential is wrong — it multiplies the
        // 15-second command timeout by the subject size — but `Promise.all`
        // over every target puts N command documents, N snapshot listeners and
        // N Eventarc deliveries in flight at once, each holding its own
        // timeout. The old chunked batch had a ceiling of ~225 items per
        // commit; dropping it along with the batch removed a bound nobody
        // decided to remove. Subjects are small in practice, so this is robustness rather
        // than a live fix.
        const results = await mapWithLimit(targets, ACCEPT_CONCURRENCY, ({ task, target }) =>
          submitTaskCreate({
            proposalPath: task.path as string,
            taskId: target.taskId,
            projectId: target.projectId,
          }),
        )

        const accepted = results.filter(Boolean).length
        if (accepted === 0) return
        if (accepted < targets.length) {
          // Not a failure to report here — each command already surfaced its own
          // outcome through §4.5's dispatch. Logged so the gap between "accept
          // all" and what arrived is visible when the board is read later.
          logger.warn(
            `acceptAllInSubject: ${accepted}/${targets.length} created; the rest failed or were unresolved`,
          )
        }

        const firstPath = targets[0].task.path
        if (firstPath) {
          await this.recalculateAggregateReviewCountsFromTaskPath(firstPath)
        }
        const bulkPid = targets[0].target.projectId
        try { trackAction(CsmAction.TaskAccepted, { projectId: bulkPid, projectMeta: { count: accepted } }) } catch { /* best-effort */ }
      } catch (error) {
        logger.error('Error in acceptAllInSubject', error)
        const { useAppStore } = await import('./app')
        useAppStore().toast('Error', 'Failed to accept tasks', 'error')
        throw error
      }
    },
    rejectAllInSubject: async function (
      subject: TaskGroupingSubject,
      allTasks: Task[],
      comment: string | null = null,
    ): Promise<void> {
      try {
        const pendingTasks = this._tasksInSubject(subject, allTasks).filter((t) => t.effectiveDecision !== 'rejected')
        if (pendingTasks.length === 0) return

        const targets = pendingTasks.filter((t) => proposalTarget(t.path) !== null)
        if (targets.length === 0) return

        // N commands, not one batch (MYAC-1061). There is no bulk command kind
        // and inventing one was out of scope, so this fans out the same
        // single-task route the toolbar's own reject uses — which is the point:
        // the batch it replaces deleted each Kanban task, and that delete is
        // what `allow delete: if false` closes.
        //
        // Bounded exactly as `acceptAllInSubject` is, and for the reason stated
        // there: sequential multiplies the command timeout by the subject size,
        // while an unbounded `Promise.all` puts N command docs, N listeners and
        // N Eventarc deliveries in flight at once.
        //
        // `quiet` because each refusal would otherwise toast on its own — three
        // failures in a forty-task subject meant four toasts, the summary lost
        // among them. The refusals are still classified, still logged, still
        // thrown; only their individual messages are withheld, and this
        // function says the one thing worth saying instead.
        const results = await mapWithLimit(targets, ACCEPT_CONCURRENCY, async (task) => {
          try {
            await this.updateTaskReview(
              task.path as string,
              { review_decision: 'rejected' as TaskReviewDecision, review_rejection_comment: comment },
              { quiet: true },
            )
            return true
          } catch {
            // Caught PER ITEM so one refusal cannot abandon the rest. The old
            // batch was all-or-nothing by construction; a partial result is now
            // a normal outcome and has to be reported rather than thrown.
            return false
          }
        })

        const done = results.filter(Boolean).length
        const failed = results.length - done

        if (failed > 0) {
          // Names to the log, count to the user: three task names read fine in
          // a toast and thirty do not, and the failing set is what support
          // needs rather than what the user does.
          logger.error(
            `rejectAllInSubject: ${done}/${results.length} rejected; failed: `
            + targets.filter((_, i) => !results[i]).map((t) => t.path).join(', '),
          )
          const { useAppStore } = await import('./app')
          useAppStore().toast(
            t('common.error'),
            done === 0
              ? t('tasks.bulk.reject_none', { failed })
              : t('tasks.bulk.reject_partial', { done, failed }),
            done === 0 ? 'error' : 'warn',
          )
        }

        if (done === 0) return

        const firstPath = targets[0]?.path
        if (firstPath) {
          await this.recalculateAggregateReviewCountsFromTaskPath(firstPath)
        }
        const bulkPid = firstPath?.split('/')[1] // projects/{pid}/...
        if (bulkPid) {
          // What actually landed, not what was attempted — same rule as accept.
          try { trackAction(CsmAction.TaskRejected, { projectId: bulkPid, projectMeta: { count: done } }) } catch { /* best-effort */ }
        }
      } catch (error) {
        logger.error('Error in rejectAllInSubject', error)
        const { useAppStore } = await import('./app')
        useAppStore().toast('Error', 'Failed to reject tasks', 'error')
        throw error
      }
    },
    async createDirectiveFromRejection(
      taskPath: string,
      comment: string | null,
      expiresAt: Date | null = null,
    ) {
      const sourceTask = this.allTasks.find((t) => t.path === taskPath)
        || useTDCurrentStore().allTasks.find((t: Task) => t.path === taskPath)
      if (!sourceTask) return

      const qmsMatch = taskPath.match(/^projects\/([^/]+)\//)
      if (!qmsMatch) return
      const pid = qmsMatch[1]

      try {
        const firestore = useFirestore()
        const usersStore = useUsersStore()
        const uid = usersStore.uid || null
        const cmdRef = collection(firestore, `projects/${pid}/command_queues/normalize_directive/commands`)
        await addDoc(cmdRef, {
          status: 'requested',
          payload: {
            kind: 'normalize_directive',
            source_task_name: sourceTask.name ?? '',
            source_task_context: sourceTask.context
              ? { finding: sourceTask.context.finding ?? '', risk: sourceTask.context.risk ?? '' }
              : null,
            source_task_clauses: sourceTask.clausesV2?.map((c) => ({
              number: c.number ?? '', title: c.title ?? '',
              standard: c.standard ?? '',
            })) ?? [],
            rejection_comment: comment ?? '',
            created_by_uid: uid,
            expires_at: expiresAt,
          },
          requested_by: uid,
        })
        const { useAppStore } = await import('./app')
        useAppStore().toast(
          t('tasks.reject_dialog.directive_created_title'),
          t('tasks.reject_dialog.directive_created'),
          'success',
        )
        // Optimistic +1: the directive is normalized async by the backend, so it
        // isn't in the store yet, but it will land active.
        try {
          const active = useTaskDirectivesStore().directives.filter((d) => d.is_active).length
          trackAction(CsmAction.DirectiveCreated, { projectId: pid, projectMeta: { directive_count: active + 1 } })
        } catch { /* best-effort */ }
      } catch (error) {
        logger.error('Error creating directive', error)
        const { useAppStore } = await import('./app')
        useAppStore().toast('Error', 'Failed to create directive', 'error')
      }
    },
    listCoherenceChecks: function (pid: string, aid: string) {
      if (this._coherence_checks_unsub && this._current_pid === pid && this._current_aid === aid) {
        return
      }
      if (this._coherence_checks_unsub) {
        this._coherence_checks_unsub()
        this._coherence_checks_unsub = null
      }
      if (!pid || !aid) {
        this.coherenceChecksSnapshots = null
        return
      }
      const ccCollection = collection(useFirestore(), `projects/${pid}/assessments/${aid}/coherence_checks`)
      this._coherence_checks_unsub = onSnapshot(ccCollection, (r) => {
        this.coherenceChecksSnapshots = r
      })
    },
    coherenceChecksUnsub: function () {
      if (this._coherence_checks_unsub) {
        this._coherence_checks_unsub()
        this._coherence_checks_unsub = null
      }
      this.coherenceChecksSnapshots = null
    },
    createTDAssessment: async function (
      pid: string,
      groupKeys: string[],
      emitStarted: boolean = true,
      languageFields: { language?: RunLanguageName } = companyLanguageField(),
    ): Promise<DocumentReference> {
      this.isLoading = true
      const firestore = useFirestore()
      const usersStore = useUsersStore()
      const ref = collection(firestore, `projects/${pid}/assessments/`)
      const userRef = doc(firestore, 'users', usersStore.uid)

      const docRef = await addDoc(ref, {
        status: 'draft',
        kind: 'technical_documentation',
        sections: groupKeys,
        created_at: serverTimestamp(),
        updated_at: serverTimestamp(),
        created_by: userRef,
        ...languageFields,
      })

      await updateDoc(docRef, {
        status: 'scheduled',
        updated_at: serverTimestamp(),
      })

      this.isLoading = false
      try { useAnalyticsStore().trackEvent('assessment_create_success', { pid, aid: docRef.id, kind: 'td', sections: groupKeys.length || 'all' }) } catch { /* best-effort */ }
      // A full TD run counts as assessment_started; a single-section rerun sets
      // emitStarted=false and emits td_section_rerun instead (see rerunTDSection).
      if (emitStarted) { try { trackAction(CsmAction.AssessmentStarted, { projectId: pid }) } catch { } }
      return docRef
    },
    runFullPipeline: async function (pid: string): Promise<DocumentReference> {
      return await this.createTDAssessment(pid, [])
    },
    /**
     * QMS supplier-list extraction (MYAC-690). Writes an Assessment doc with
     * kind `qms_supplier_list`, reusing the existing assessments Eventarc
     * trigger. The assessments service runs the supplier agent directly and
     * persists the result as a project-root ProjectMemory (`qms_supplier_list`),
     * which the front reads back. Mirrors `createTDAssessment`.
     */
    createSupplierExtraction: async function (pid: string): Promise<DocumentReference> {
      this.isLoading = true
      try {
        const firestore = useFirestore()
        const usersStore = useUsersStore()
        const ref = collection(firestore, `projects/${pid}/assessments/`)
        const userRef = doc(firestore, 'users', usersStore.uid)

        const docRef = await addDoc(ref, {
          status: 'draft',
          kind: 'qms_supplier_list',
          sections: [],
          created_at: serverTimestamp(),
          updated_at: serverTimestamp(),
          created_by: userRef,
          ...companyLanguageField(),
        })

        await updateDoc(docRef, {
          status: 'scheduled',
          updated_at: serverTimestamp(),
        })

        try { useAnalyticsStore().trackEvent('assessment_create_success', { pid, aid: docRef.id, kind: 'qms_supplier_list' }) } catch { /* best-effort */ }
        return docRef
      } finally {
        this.isLoading = false
      }
    },
    /** The `language` of the project's latest technical-file run; {} when it had none (English). */
    lastTDRunLanguageFields: async function (pid: string): Promise<{ language?: RunLanguageName }> {
      let runs
      try {
        runs = await getDocs(query(
          collection(useFirestore(), `projects/${pid}/assessments/`),
          where('kind', '==', 'technical_documentation'),
        ))
      } catch (e) {
        // The language is not worth blocking the rerun over.
        logger.warn('lastTDRunLanguageFields: previous runs unreadable, using the company language', e)
        return companyLanguageField()
      }
      let latest: DocumentData | null = null
      for (const snap of runs.docs) {
        const data = snap.data()
        if (!latest || (data.created_at?.toMillis?.() ?? 0) > (latest.created_at?.toMillis?.() ?? 0)) latest = data
      }
      if (latest === null) return companyLanguageField()
      const language = latest.language
      return language === 'french' || language === 'english' ? { language } : {}
    },
    rerunTDSection: async function (pid: string, sectionKey: string): Promise<DocumentReference> {
      const projectsStore = useProjectsStore()
      const regulation = (projectsStore.currentProject?.td_regulation ?? 'mdr') as TDRegulation
      const group = tdGroupForSubsection(regulation, sectionKey)
      if (!group) {
        throw new Error(`Unknown TD section key: ${sectionKey}`)
      }
      // A rerun section sits inside the technical file the last run wrote: it keeps
      // that run's language, even if the company chose another one since.
      const languageFields = await this.lastTDRunLanguageFields(pid)
      const docRef = await this.createTDAssessment(pid, [group.groupKey], false, languageFields)
      try { trackAction(CsmAction.TdSectionRerun, { projectId: pid }) } catch { }
      return docRef
    },
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAssessmentsStore, import.meta.hot))
}
